// Client for the D1-backed concert query API (Pages Functions under /api).
// Concert *records* are fetched on demand here instead of shipping the whole
// corpus to the browser; the small aggregates (cities/composers/orgs) stay static.
//
// In production the API is same-origin (/api). For `vite dev` (no Functions
// runtime), set VITE_API_URL to a deployed origin's /api to develop against it.
import type { Concert, HistoricalConcert, HistoricalMention, Piece, SourceInfo } from "../types";

// `||` (not `??`) so an empty VITE_API_URL falls back to same-origin /api.
const RAW_API_BASE = import.meta.env.VITE_API_URL || "/api";
const API_BASE = RAW_API_BASE.endsWith("/") ? RAW_API_BASE.slice(0, -1) : RAW_API_BASE;

// Data version (deploy-time constant, injected in vite.config.ts): appended to every
// /api URL as `v=` so responses can be cached for a long time by browsers and the edge —
// the URL space rotates on each deploy, which is the only time the data changes.
// "dev" when built without an export (vite dev / tests).
const DATA_VERSION: string = typeof __DATA_VERSION__ === "string" ? __DATA_VERSION__ : "dev";

/** Serialize query params with the data-version key appended. */
function vq(p: URLSearchParams): string {
  p.set("v", DATA_VERSION);
  return p.toString();
}

export interface ConcertFilters {
  city?: number | null;
  org?: number | null;
  composer?: number | null;
  /** Piece facet key (see /api/pieces) — an opaque string, not an id. */
  piece?: string | null;
  /** Genre facet value (see FACET_GENRES in api/export.py); "" / null = no filter. */
  genre?: string | null;
  country?: string | null;
  from?: string | null;
  to?: string | null;
  /** [west, south, east, north] */
  bbox?: [number, number, number, number] | null;
  /** Radius search center [lat, lon] — only sent together with `km`. Replaces the exact
   *  `city` match when the user widens a city filter to "within N km". */
  near?: [number, number] | null;
  km?: number | null;
  /** Sort direction on date_start (server default asc). The backward ("Past") list reads
   *  desc. SQLite sorts NULL dates first under asc and last under desc — the client-side
   *  merge comparator mirrors exactly that. */
  order?: "asc" | "desc";
}

export interface ConcertPage {
  concerts: Concert[];
  total: number;
  /** How many of `total` are cancelled/postponed: listed, not counted (ISSUES #069). */
  notPerformed: number;
  offset: number;
  hasMore: boolean;
}

/** Counts-only filters: country/viewport are applied client-side, so they're absent.
 *  `anchor` is the list's Upcoming/Past split date (the client's today) — when set, the
 *  server splits each city's count at it so the popup can quote the list's numbers. */
export type CountFilters = Pick<
  ConcertFilters,
  "composer" | "piece" | "genre" | "org" | "from" | "to"
> & {
  anchor?: string | null;
};

/** Per-city live counts: `n` over the whole window; `up` = the on/after-anchor share of
 *  `n`, or null when no anchor was sent (window excludes today). */
export interface CityCount {
  n: number;
  up: number | null;
}

function appendFilters(p: URLSearchParams, f: ConcertFilters): void {
  if (f.composer != null) p.set("composer", String(f.composer));
  if (f.piece) p.set("piece", f.piece);
  if (f.genre) p.set("genre", f.genre);
  if (f.org != null) p.set("org", String(f.org));
  if (f.city != null) p.set("city", String(f.city));
  if (f.country) p.set("country", f.country);
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  if (f.bbox) p.set("bbox", f.bbox.join(","));
  if (f.near && f.km != null) {
    p.set("near", f.near.join(","));
    p.set("km", String(f.km));
  }
  if (f.order === "desc") p.set("order", "desc");
}

export function fetchConcerts(
  f: ConcertFilters,
  opts: { limit?: number; offset?: number; signal?: AbortSignal } = {},
): Promise<ConcertPage> {
  return fetchListingPage("concerts", f, opts);
}

/** The opt-in rows behind the "non-classical and kids' events" toggle: same filters and paging. */
export function fetchExtraConcerts(
  f: ConcertFilters,
  opts: { limit?: number; offset?: number; signal?: AbortSignal } = {},
): Promise<ConcertPage> {
  return fetchListingPage("extra-concerts", f, opts);
}

async function fetchListingPage(
  endpoint: "concerts" | "extra-concerts",
  f: ConcertFilters,
  opts: { limit?: number; offset?: number; signal?: AbortSignal },
): Promise<ConcertPage> {
  const p = new URLSearchParams();
  appendFilters(p, f);
  if (opts.limit != null) p.set("limit", String(opts.limit));
  if (opts.offset) p.set("offset", String(opts.offset));

  const res = await fetch(`${API_BASE}/${endpoint}?${vq(p)}`, { signal: opts.signal });
  if (!res.ok) throw new Error(`/api/${endpoint} returned ${res.status}`);
  const body = (await res.json()) as {
    concerts?: Concert[];
    total?: number;
    not_performed?: number;
    offset?: number;
    has_more?: boolean;
  };
  return {
    concerts: body.concerts ?? [],
    total: body.total ?? 0,
    notPerformed: body.not_performed ?? 0,
    offset: body.offset ?? opts.offset ?? 0,
    hasMore: Boolean(body.has_more),
  };
}

/** Search piece facets. Unlike composers/cities/orgs (searched in-browser against static
 *  JSON), pieces are searched server-side — there are ~33k of them, far too many to ship.
 *
 *  Pass `composer` to scope the list to one composer's works; results are ranked by how
 *  often the piece is performed, so an empty query returns a useful popular head. */
export async function fetchPieces(
  query: string,
  opts: { limit?: number; offset?: number; composer?: number | null; signal?: AbortSignal } = {},
): Promise<Piece[]> {
  const p = new URLSearchParams();
  if (query.trim()) p.set("q", query.trim());
  if (opts.composer != null) p.set("composer", String(opts.composer));
  if (opts.limit != null) p.set("limit", String(opts.limit));
  if (opts.offset) p.set("offset", String(opts.offset));

  const res = await fetch(`${API_BASE}/pieces?${vq(p)}`, { signal: opts.signal });
  if (!res.ok) throw new Error(`/api/pieces returned ${res.status}`);
  const body = (await res.json()) as { pieces?: Piece[] };
  return body.pieces ?? [];
}

/** Resolve one facet key back to its full record — how a shared `?piece=` URL recovers its
 *  label, since the client never holds the facet list. Null when the key no longer exists
 *  (an export can retire a key: the last performance of that piece rolls out of the data). */
export async function fetchPieceByKey(
  key: string,
  opts: { signal?: AbortSignal } = {},
): Promise<Piece | null> {
  const res = await fetch(`${API_BASE}/pieces?${vq(new URLSearchParams({ key }))}`, {
    signal: opts.signal,
  });
  if (!res.ok) throw new Error(`/api/pieces returned ${res.status}`);
  const body = (await res.json()) as { pieces?: Piece[] };
  return body.pieces?.[0] ?? null;
}

// ── Archive mode ─────────────────────────────────────────────────

export interface HistoricalConcertFilters {
  /** ISO date. The window is an OVERLAP filter server-side — see historical-concerts.ts —
   *  not a point comparison, so a decade-precision event partly inside the window is kept. */
  from?: string | null;
  to?: string | null;
  composer?: number | null;
  /** Piece facet key (see /api/pieces) — the archive side joins historical_concert_pieces. */
  piece?: string | null;
  city?: number | null;
  country?: string | null;
  /** Source keys to EXCLUDE (the Sources facet stores exclusions so new sources default on). */
  xsrc?: string[] | null;
  bbox?: [number, number, number, number] | null;
  /** Radius search center [lat, lon] + km — same semantics as ConcertFilters. */
  near?: [number, number] | null;
  km?: number | null;
  /** Sort direction on date_earliest (server default asc). */
  order?: "asc" | "desc";
  /** Certainty tiers to include (the Certainty control). Omit ⇒ server default
   *  confirmed+probable. Add "possible" to surface fuzzy-date memoir events. */
  cert?: string[] | null;
}

export interface HistoricalConcertPage {
  concerts: HistoricalConcert[];
  total: number;
  offset: number;
  hasMore: boolean;
}

function appendHistoricalFilters(p: URLSearchParams, f: HistoricalConcertFilters): void {
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  if (f.composer != null) p.set("composer", String(f.composer));
  if (f.piece) p.set("piece", f.piece);
  if (f.city != null) p.set("city", String(f.city));
  if (f.country) p.set("country", f.country);
  if (f.xsrc && f.xsrc.length > 0) p.set("xsrc", f.xsrc.join(","));
  if (f.bbox) p.set("bbox", f.bbox.join(","));
  if (f.near && f.km != null) {
    p.set("near", f.near.join(","));
    p.set("km", String(f.km));
  }
  if (f.order === "desc") p.set("order", "desc");
  // Only serialize a non-default certainty set — the server default is confirmed+probable.
  if (f.cert && f.cert.length > 0 && f.cert.includes("possible")) p.set("cert", f.cert.join(","));
}

/** Absolute-path URL of a CSV export (see /api-docs/). `limit` counts concerts/events. Carries
 *  the deploy's `v=` like every other app request, so a download right after a deploy gets a
 *  fresh edge-cache key instead of a stale-while-revalidate copy of the previous data. */
export function csvExportUrl(
  kind: "live" | "extra" | "archive",
  filters: ConcertFilters | HistoricalConcertFilters,
  opts: { rows: string; limit: number; offset?: number; bom?: boolean },
): string {
  const p = new URLSearchParams();
  if (kind === "archive") appendHistoricalFilters(p, filters as HistoricalConcertFilters);
  else appendFilters(p, filters as ConcertFilters);
  p.set("rows", opts.rows);
  p.set("limit", String(opts.limit));
  if (opts.offset) p.set("offset", String(opts.offset));
  if (opts.bom) p.set("bom", "1");
  const endpoint = kind === "live" ? "concerts" : kind === "extra" ? "extra-concerts" : "historical-concerts";
  return `${API_BASE}/${endpoint}.csv?${vq(p)}`;
}

export async function fetchHistoricalConcerts(
  f: HistoricalConcertFilters,
  opts: { limit?: number; offset?: number; signal?: AbortSignal } = {},
): Promise<HistoricalConcertPage> {
  const p = new URLSearchParams();
  appendHistoricalFilters(p, f);
  if (opts.limit != null) p.set("limit", String(opts.limit));
  if (opts.offset) p.set("offset", String(opts.offset));

  const res = await fetch(`${API_BASE}/historical-concerts?${vq(p)}`, { signal: opts.signal });
  if (!res.ok) throw new Error(`/api/historical-concerts returned ${res.status}`);
  const body = (await res.json()) as {
    concerts?: HistoricalConcert[];
    total?: number;
    offset?: number;
    has_more?: boolean;
  };
  return {
    concerts: body.concerts ?? [],
    total: body.total ?? 0,
    offset: body.offset ?? opts.offset ?? 0,
    hasMore: Boolean(body.has_more),
  };
}

export type HistoricalCountFilters = Pick<
  HistoricalConcertFilters,
  "from" | "to" | "composer" | "piece" | "xsrc" | "cert"
>;

/** The Sources facet's data: every historical source with counts and year span.
 *
 *  ⚠ The app no longer calls this — `loader.ts` reads the precomputed
 *  `/data/historical_sources.json` instead, because the underlying query scanned all 130,711
 *  historical_concerts rows (1.59 s of D1 for 1.8 KB) on every landing load. Kept so older
 *  cached bundles still resolve, and as the reference implementation the export mirrors. */
export async function fetchHistoricalSources(
  opts: { signal?: AbortSignal } = {},
): Promise<SourceInfo[]> {
  const res = await fetch(`${API_BASE}/historical-sources?${vq(new URLSearchParams())}`, { signal: opts.signal });
  if (!res.ok) throw new Error(`/api/historical-sources returned ${res.status}`);
  const body = (await res.json()) as { sources?: SourceInfo[] };
  return body.sources ?? [];
}

export async function fetchHistoricalCityCounts(
  f: HistoricalCountFilters,
  opts: { signal?: AbortSignal } = {},
): Promise<Map<number, number>> {
  const p = new URLSearchParams();
  appendHistoricalFilters(p, f);

  const res = await fetch(`${API_BASE}/historical-city-counts?${vq(p)}`, { signal: opts.signal });
  if (!res.ok) throw new Error(`/api/historical-city-counts returned ${res.status}`);
  const body = (await res.json()) as { counts?: { city_id: number; n: number }[] };
  const map = new Map<number, number>();
  for (const c of body.counts ?? []) map.set(c.city_id, c.n);
  return map;
}

// ── Mentions Reader ──────────────────────────────────────────────

export interface HistoricalMentionPage {
  mentions: HistoricalMention[];
  total: number;
  offset: number;
  hasMore: boolean;
}

/** Grounded-quote mentions for one composer (the Mentions Reader). `work` narrows to one
 *  catalogue work (piece-scoped Reader); `source` optionally narrows to one memoir;
 *  `undated` restricts to the placeless/dateless material. */
export async function fetchHistoricalMentions(
  f: { composer?: number | null; work?: number | null; source?: string | null; undated?: boolean },
  opts: { limit?: number; offset?: number; signal?: AbortSignal } = {},
): Promise<HistoricalMentionPage> {
  const p = new URLSearchParams();
  if (f.composer != null) p.set("composer", String(f.composer));
  if (f.work != null) p.set("work", String(f.work));
  if (f.source) p.set("source", f.source);
  if (f.undated) p.set("undated", "1");
  if (opts.limit != null) p.set("limit", String(opts.limit));
  if (opts.offset) p.set("offset", String(opts.offset));

  const res = await fetch(`${API_BASE}/historical-mentions?${vq(p)}`, { signal: opts.signal });
  if (!res.ok) throw new Error(`/api/historical-mentions returned ${res.status}`);
  const body = (await res.json()) as {
    mentions?: HistoricalMention[];
    total?: number;
    offset?: number;
    has_more?: boolean;
  };
  return {
    mentions: body.mentions ?? [],
    total: body.total ?? 0,
    offset: body.offset ?? opts.offset ?? 0,
    hasMore: Boolean(body.has_more),
  };
}

export function fetchCityCounts(
  f: CountFilters,
  opts: { signal?: AbortSignal } = {},
): Promise<Map<number, CityCount>> {
  return fetchCounts("city-counts", f, opts);
}

/** Marker counts of the opt-in rows; the caller adds them to fetchCityCounts' with addCityCounts. */
export function fetchExtraCityCounts(
  f: CountFilters,
  opts: { signal?: AbortSignal } = {},
): Promise<Map<number, CityCount>> {
  return fetchCounts("extra-city-counts", f, opts);
}

export function addCityCounts(a: Map<number, CityCount>, b: Map<number, CityCount>): Map<number, CityCount> {
  const sum = new Map(a);
  for (const [id, c] of b) {
    const prior = sum.get(id);
    sum.set(id, prior ? { n: prior.n + c.n, up: prior.up === null || c.up === null ? null : prior.up + c.up } : c);
  }
  return sum;
}

async function fetchCounts(
  endpoint: "city-counts" | "extra-city-counts",
  f: CountFilters,
  opts: { signal?: AbortSignal },
): Promise<Map<number, CityCount>> {
  const p = new URLSearchParams();
  appendFilters(p, f);
  if (f.anchor) p.set("anchor", f.anchor);

  const res = await fetch(`${API_BASE}/${endpoint}?${vq(p)}`, { signal: opts.signal });
  if (!res.ok) throw new Error(`/api/${endpoint} returned ${res.status}`);
  const body = (await res.json()) as {
    counts?: { city_id: number; n: number; up?: number | null }[];
  };
  const map = new Map<number, CityCount>();
  for (const c of body.counts ?? []) map.set(c.city_id, { n: c.n, up: c.up ?? null });
  return map;
}

// ── Facets (Explore) ─────────────────────────────────────────────

export type FacetEra = "live" | "archive";

/** Filters for /api/facets: the browse vocabulary plus the archive params and the three
 *  Explore-only narrowings (see functions/api/_facets.ts). */
export interface FacetFilters
  extends Pick<ConcertFilters, "composer" | "org" | "piece" | "genre" | "city" | "country" | "from" | "to" | "near" | "km"> {
  xsrc?: string[];
  cert?: string[];
  xera?: number | null;
  alive?: boolean;
  premiere?: boolean;
  /** Pieces under a composer filter: the composer's own ("only") or everyone else's ("others"). */
  own?: "only" | "others" | null;
}

export interface FacetBucket {
  k: string | number | null;
  k2?: string | number | null;
  n: number;
  /** Concerts on/after the anchor — present when one was sent. */
  up?: number | null;
  /** Piece buckets carry their label (the client holds no piece list). */
  label?: string | null;
  composer?: string | null;
  composer_id?: number | null;
}

export interface FacetResult {
  era: FacetEra;
  group: string;
  by: string | null;
  buckets: FacetBucket[];
  /** Size of the filtered slice (what the buckets are shares of). */
  matched: number;
  matchedUp: number | null;
  truncated: boolean;
  shape: string;
  cost: { rows_read: number; ms: number };
}

export async function fetchFacets(
  era: FacetEra,
  group: string,
  f: FacetFilters,
  opts: {
    by?: string | null;
    keys?: string[] | null;
    limit?: number;
    offset?: number;
    anchor?: string | null;
    signal?: AbortSignal;
  } = {},
): Promise<FacetResult> {
  const p = new URLSearchParams();
  p.set("era", era);
  p.set("group", group);
  if (opts.by) p.set("by", opts.by);
  if (f.composer != null) p.set("composer", String(f.composer));
  if (f.org != null) p.set("org", String(f.org));
  if (f.piece) p.set("piece", f.piece);
  if (f.genre) p.set("genre", f.genre);
  if (f.city != null) p.set("city", String(f.city));
  if (f.country) p.set("country", f.country);
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  if (f.near && f.km != null) {
    p.set("near", f.near.join(","));
    p.set("km", String(f.km));
  }
  if (f.xsrc?.length) p.set("xsrc", [...f.xsrc].sort().join(","));
  if (f.cert?.length) p.set("cert", f.cert.join(","));
  if (f.xera != null) p.set("xera", String(f.xera));
  if (f.alive) p.set("alive", "1");
  if (f.premiere) p.set("premiere", "1");
  if (f.own) p.set("own", f.own);
  if (opts.keys?.length) p.set("keys", opts.keys.join(","));
  if (opts.limit != null) p.set("limit", String(opts.limit));
  if (opts.offset) p.set("offset", String(opts.offset));
  if (opts.anchor) p.set("anchor", opts.anchor);
  // Canonical param order so equivalent requests share one edge-cache entry.
  p.sort();

  const res = await fetch(`${API_BASE}/facets?${vq(p)}`, { signal: opts.signal });
  if (!res.ok) {
    let detail = "";
    try {
      detail = ((await res.json()) as { error?: string }).error ?? "";
    } catch {
      /* body wasn't JSON */
    }
    throw new Error(`/api/facets returned ${res.status}${detail ? `: ${detail}` : ""}`);
  }
  const body = (await res.json()) as {
    era: FacetEra;
    group: string;
    by: string | null;
    buckets?: FacetBucket[];
    matched?: number;
    matched_up?: number | null;
    truncated?: boolean;
    shape?: string;
    cost?: { rows_read: number; ms: number };
  };
  return {
    era: body.era,
    group: body.group,
    by: body.by ?? null,
    buckets: body.buckets ?? [],
    matched: body.matched ?? 0,
    matchedUp: body.matched_up ?? null,
    truncated: Boolean(body.truncated),
    shape: body.shape ?? "",
    cost: body.cost ?? { rows_read: 0, ms: 0 },
  };
}

// ── Entity profiles (Explore) ────────────────────────────────────

/** What the works catalogue knows about one piece facet beyond its label — the header
 *  of the piece's Explore profile. Every field past `count` can be null: coverage is
 *  uneven (a catalogue number for ~two thirds of the most-performed works, a composed
 *  year for about half, a Wikidata id for ~40%) and the header shows only what it has. */
export interface PieceEntity extends Piece {
  work_id: number | null;
  genre: string | null;
  /** Display form: "Op. 67", "K. 550", "BWV 1007", "Hob. I:94". */
  catalogue: string | null;
  /** Composed year(s) as a string: "1808", "1873-1874". */
  year: string | null;
  /** The parent work of a part (an opera for its overture) — `parent_key` only when the
   *  parent is itself a facet the reader can open. */
  parent_key: string | null;
  parent_label: string | null;
  wikidata: string | null;
  gnd: string | null;
}

export interface ComposerEntity {
  id: number;
  birth_year: number | null;
  death_year: number | null;
  wikidata: string | null;
  gnd: string | null;
  viaf: string | null;
  /** Distinct piece facets credited to this composer in the export. */
  pieces: number;
}

export async function fetchPieceEntity(
  key: string,
  opts: { signal?: AbortSignal } = {},
): Promise<PieceEntity | null> {
  const res = await fetch(`${API_BASE}/entity?${vq(new URLSearchParams({ piece: key }))}`, { signal: opts.signal });
  if (!res.ok) throw new Error(`/api/entity returned ${res.status}`);
  const body = (await res.json()) as { piece?: PieceEntity | null };
  return body.piece ?? null;
}

export async function fetchComposerEntity(
  id: number,
  opts: { signal?: AbortSignal } = {},
): Promise<ComposerEntity | null> {
  const res = await fetch(`${API_BASE}/entity?${vq(new URLSearchParams({ composer: String(id) }))}`, { signal: opts.signal });
  if (!res.ok) throw new Error(`/api/entity returned ${res.status}`);
  const body = (await res.json()) as { composer?: ComposerEntity | null };
  return body.composer ?? null;
}

/** English Wikipedia article for a Wikidata item, resolved by Wikidata itself — no
 *  sitelink lookup needed client-side. */
export function wikipediaUrl(qid: string): string {
  return `https://www.wikidata.org/wiki/Special:GoToLinkedPage/enwiki/${encodeURIComponent(qid)}`;
}
